import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { usePurchaseInvoiceContext } from "../../../context/PurchaseInvoiceContext";
import { usePaymentVoucherContext } from "../../../context/PaymentVoucherContext";
import { formatDate } from "../../../utils/dateFormatter";
import { BranchContactHeader } from "../../../components/branchContact/BranchContact";
import "./purchaseInvoiceDetails.css";

const PurchaseInvoiceDetails = () => {
  const { id } = useParams();
  const { getPurchaseInvoice, cancelPurchaseInvoice } =
    usePurchaseInvoiceContext();
  const { fetchInvoicePaymentVouchers } = usePaymentVoucherContext();
  const [invoice, setInvoice] = useState(null);
  const [paymentVouchers, setPaymentVouchers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInvoiceDetails = async () => {
    try {
      const response = await getPurchaseInvoice(id);
      if (response.status === "success") {
        setInvoice(response.invoice);

        // جلب سندات الصرف
        const vouchers = await fetchInvoicePaymentVouchers(id);
        setPaymentVouchers(vouchers);
      }
    } catch (err) {
      console.error("Error fetching invoice:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceDetails();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  const handleCancelInvoice = async () => {
    if (
      !window.confirm(
        "هل أنت متأكد من إلغاء هذه الفاتورة؟\nسيتم خصم الكميات وتحديث الرصيد."
      )
    ) {
      return;
    }

    try {
      const response = await cancelPurchaseInvoice(id);

      if (response.status === "success") {
        alert("✅ " + response.message);
        fetchInvoiceDetails(); // إعادة تحميل تفاصيل الفاتورة
      }
    } catch (err) {
      alert("❌ " + (err.response?.data?.message || "حدث خطأ أثناء الإلغاء"));
    }
  };

  if (loading) {
    return (
      <div className="invoice-details-page">
        <div className="loading-container">
          <p>جارٍ تحميل تفاصيل الفاتورة...</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="invoice-details-page">
        <div className="error-container">
          <p>لم يتم العثور على الفاتورة</p>
          <Link to="/purchase-invoices">العودة للفواتير</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="invoice-details-page">
      <div className="invoice-container">
        {/* Action Buttons */}
        <div className="action-buttons no-print">
          <Link to="/purchase-invoices" className="back-btn">
            ← العودة للفواتير
          </Link>
          <button className="print-btn" onClick={handlePrint}>
            🖨️ طباعة الفاتورة
          </button>
          {invoice.is_canceled != 1 && (
            <button
              className="cancel-btn"
              onClick={handleCancelInvoice}
              style={{
                backgroundColor: "#ef4444",
                color: "white",
                padding: "10px 20px",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "16px",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#dc2626";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#ef4444";
              }}
            >
              ❌ إلغاء الفاتورة
            </button>
          )}
          {invoice.is_canceled == 1 && (
            <div
              style={{
                padding: "12px 24px",
                backgroundColor: "#fee2e2",
                color: "#dc2626",
                borderRadius: "8px",
                fontWeight: "700",
                fontSize: "18px",
                border: "2px solid #dc2626",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              ⚠️ هذه الفاتورة ملغية
            </div>
          )}
        </div>

        <BranchContactHeader branch={invoice.branch} />

        {/* Invoice Header */}
        <div className="purchase-invoice-header">
          <h1>فاتورة شراء رقم #{invoice.id}</h1>
          <div className="invoice-date">
            التاريخ: {formatDate(invoice.created_at)}
          </div>
        </div>

        {/* Supplier Info */}
        <div className="info-section">
          <h2>معلومات المورد</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="label">الاسم:</span>
              <span className="value">
                <a
                  href={`/supplier/${invoice.supplier_id}`}
                  style={{
                    color: "#1976d2",
                    textDecoration: "none",
                    fontWeight: 500,
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.textDecoration = "underline")
                  }
                  onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
                >
                  {invoice.supplier_name}
                </a>
              </span>
            </div>
            <div className="info-item">
              <span className="label">الهاتف:</span>
              <span className="value">{invoice.supplier_phone}</span>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="items-section">
          <h2>الأصناف</h2>
          <table className="items-table">
            <thead>
              <tr>
                <th>#</th>
                <th>اسم الصنف</th>
                <th>الكمية</th>
                <th>سعر الوحدة</th>
                <th>المجموع</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td>{item.product_name}</td>
                  <td>{item.quantity}</td>
                  <td>{parseFloat(item.unit_price).toFixed(2)} ₪</td>
                  <td>{parseFloat(item.subtotal).toFixed(2)} ₪</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="4" className="total-label">
                  المجموع الإجمالي:
                </td>
                <td className="total-amount">
                  {parseFloat(invoice.total_amount).toFixed(2)} ₪
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div className="invoice-notes">
            <h2>ملاحظات</h2>
            <p>{invoice.notes}</p>
          </div>
        )}

        {/* Payment Vouchers */}
        {!invoice.is_canceled && paymentVouchers.length > 0 && (
          <div className="vouchers-section no-print">
            <h2>سندات الصرف</h2>
            <div className="vouchers-list">
              {paymentVouchers.map((voucher) => (
                <div key={voucher.id} className="voucher-card">
                  <div className="voucher-header-row">
                    <Link
                      to={`/payment-voucher/${voucher.id}`}
                      className="voucher-id"
                      style={{
                        cursor: "pointer",
                        color: "#0066ff",
                        textDecoration: "none",
                      }}
                    >
                      سند صرف #{voucher.id}
                    </Link>
                    <span className="voucher-date">
                      {formatDate(voucher.created_at)}
                    </span>
                  </div>
                  <div className="voucher-details">
                    <div className="voucher-row">
                      <span className="label">المبلغ:</span>
                      <span className="value amount">
                        {parseFloat(voucher.amount).toFixed(2)} ₪
                      </span>
                    </div>
                    <div className="voucher-row">
                      <span className="label">نوع الدفع:</span>
                      <span className="value">
                        <span
                          style={{
                            padding: "2px 8px",
                            borderRadius: "8px",
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            backgroundColor:
                              voucher.payment_type === "كاش"
                                ? "#d1fae5"
                                : "#dbeafe",
                            color:
                              voucher.payment_type === "كاش"
                                ? "#065f46"
                                : "#1e40af",
                          }}
                        >
                          {voucher.payment_type}
                        </span>
                      </span>
                    </div>
                    {voucher.payment_type === "شيك" && (
                      <div className="check-info">
                        <div className="voucher-row">
                          <span className="label">رقم الشيك:</span>
                          <span className="value">{voucher.check_number}</span>
                        </div>
                        <div className="voucher-row">
                          <span className="label">البنك:</span>
                          <span className="value">{voucher.check_bank}</span>
                        </div>
                        <div className="voucher-row">
                          <span className="label">تاريخ الاستحقاق:</span>
                          <span className="value">
                            {formatDate(voucher.check_due_date)}
                          </span>
                        </div>
                      </div>
                    )}
                    {voucher.notes && (
                      <div className="voucher-row">
                        <span className="label">ملاحظات:</span>
                        <span className="value">{voucher.notes}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="remaining-amount">
              <span className="label">المبلغ المتبقي:</span>
              <span className="value">
                {(
                  parseFloat(invoice.total_amount) -
                  paymentVouchers.reduce(
                    (sum, v) => sum + parseFloat(v.amount),
                    0
                  )
                ).toFixed(2)}{" "}
                ₪
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseInvoiceDetails;
